import { ref, watch, type Ref } from "vue";
import { ProfileAdministrationAPI } from "../api";
import type { Participant, Project, ProjectRole } from "../types/PersologOnlineTypes";
import { fetchRoleTypes, fetchRoles } from "../utils/ProjectUtils";

/**
 * Whether moving [participant] into the selected [target] loses their role (RDT-114).
 *
 * The backend carries the role over: it reuses the target's role of the same type or activates it
 * there. Only a type the target's product does not offer cannot be activated, and then the
 * participant arrives without a role. `droppedRole` holds that role's display name so the dialog can
 * say so before the move, and is null whenever the role survives (or there is none).
 */
export function useMoveRoleCheck(participant: Ref<Participant>, project: Ref<Project>, target: Ref<Project | null>) {
    const droppedRole = ref<string | null>(null);
    let sourceRoles: Promise<ProjectRole[]> | null = null;
    let latest = 0;

    watch(target, async (next) => {
        const request = ++latest;
        droppedRole.value = null;
        const roleId = participant.value.roleId;
        if (!next?.id || !roleId) return;

        try {
            sourceRoles ??= fetchRoles(project.value);
            const [roles, targetRoles, available] = await Promise.all([
                sourceRoles,
                ProfileAdministrationAPI.getRolesForProject(next.id),
                fetchRoleTypes(next.product),
            ]);
            const role = roles.find((r) => r.id === roleId);
            if (!role || request !== latest) return;

            const kept =
                targetRoles.some((r) => r.role === role.role) || available.some((r) => r.type === role.role);
            droppedRole.value = kept ? null : role.name || role.role;
        } catch (error) {
            // Only a hint: the move itself does not depend on it.
            console.error("Error checking the participant's role for the move:", error);
        }
    });

    return { droppedRole };
}
